/**
 * Pretty-prints a tool_result observation that came back as a
 * `validation_failed` payload (formatIssues output). Splits the raw text into
 * severity buckets, colors them, and turns `parts=X,Y` references into copyable
 * chips so the user can find the offending parts in chat messages.
 *
 * The observation format from functions/src/hooks/index.ts formatIssues():
 *   errors (2):
 *     [code] message (parts=A,B; joints=J1) → fix: ...
 *   warnings (1):
 *     [code] message
 */
import { useState } from 'react';
import { AlertTriangle, AlertCircle, Info, Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type ParsedIssue = {
  code: string;
  message: string;
  partIds: string[];
  jointIds: string[];
  fix?: string;
};

type ParsedSection = {
  severity: 'error' | 'warning' | 'info';
  issues: ParsedIssue[];
};

function parseObservation(observation: string): {
  header: string;
  sections: ParsedSection[];
  trailing: string;
} {
  const lines = observation.split('\n');
  const sections: ParsedSection[] = [];
  let header = '';
  let trailing = '';
  let cur: ParsedSection | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const sevMatch = /^(errors?|warnings?|infos?)\s*\(\d+\):\s*$/i.exec(line.trim());
    if (sevMatch) {
      const sevWord = sevMatch[1].toLowerCase();
      const severity = sevWord.startsWith('error')
        ? 'error'
        : sevWord.startsWith('warn')
          ? 'warning'
          : 'info';
      cur = { severity, issues: [] };
      sections.push(cur);
      continue;
    }
    if (!cur) {
      // Pre-section text (e.g., "validation_failed (part not added):")
      header += (header ? '\n' : '') + line;
      continue;
    }
    const issueMatch = /^\s+\[([^\]]+)\]\s+(.*)$/.exec(line);
    if (issueMatch) {
      const code = issueMatch[1];
      let rest = issueMatch[2];
      let fix: string | undefined;
      const fixIdx = rest.indexOf(' → fix: ');
      if (fixIdx >= 0) {
        fix = rest.slice(fixIdx + ' → fix: '.length);
        rest = rest.slice(0, fixIdx);
      }
      // Pull (parts=X,Y; joints=J1) tail if present.
      const refMatch = /\s+\((parts|joints)=([^)]+)\)\s*$/.exec(rest);
      let partIds: string[] = [];
      let jointIds: string[] = [];
      if (refMatch) {
        rest = rest.slice(0, refMatch.index);
        const refSegments = refMatch[0].slice(2, -1).split(';').map((s) => s.trim());
        for (const seg of refSegments) {
          const m = /^(parts|joints)=(.+)$/.exec(seg);
          if (!m) continue;
          const ids = m[2].split(',').map((s) => s.trim()).filter(Boolean);
          if (m[1] === 'parts') partIds = ids;
          else jointIds = ids;
        }
      }
      cur.issues.push({ code, message: rest.trim(), partIds, jointIds, fix });
    } else if (cur.issues.length > 0) {
      // Continuation of the previous issue's message
      cur.issues[cur.issues.length - 1].message += '\n' + line.trim();
    } else {
      trailing += (trailing ? '\n' : '') + line;
    }
  }

  return { header: header.trim(), sections, trailing: trailing.trim() };
}

export function ValidationObservation({ observation }: { observation: string }) {
  const { header, sections, trailing } = parseObservation(observation);
  if (sections.length === 0) {
    return (
      <pre className="whitespace-pre-wrap break-words text-synergy-neutral-400 text-[11px] leading-relaxed">
        {observation}
      </pre>
    );
  }
  return (
    <div className="flex flex-col gap-2 text-[11px]">
      {header && (
        <div className="font-medium text-synergy-neutral-300">{header}</div>
      )}
      {sections.map((s, idx) => (
        <SeveritySection key={idx} section={s} />
      ))}
      {trailing && (
        <div className="text-synergy-neutral-500 italic">{trailing}</div>
      )}
    </div>
  );
}

function SeveritySection({ section }: { section: ParsedSection }) {
  const { color, Icon } = severityStyles(section.severity);
  return (
    <div className="flex flex-col gap-1.5">
      <div className={cn('flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold', color)}>
        <Icon className="h-3 w-3" />
        <span>
          {section.severity} ({section.issues.length})
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {section.issues.map((issue, i) => (
          <IssueRow key={i} issue={issue} severity={section.severity} />
        ))}
      </div>
    </div>
  );
}

function IssueRow({
  issue,
  severity,
}: {
  issue: ParsedIssue;
  severity: ParsedSection['severity'];
}) {
  const { ring } = severityStyles(severity);
  return (
    <div className={cn('rounded-md border bg-synergy-neutral-900/50 px-2 py-1.5 text-synergy-neutral-300', ring)}>
      <div className="flex flex-wrap items-baseline gap-1.5">
        <code className="rounded bg-synergy-neutral-800/80 px-1 py-0.5 font-mono text-[9px] tracking-wide text-synergy-neutral-200">
          {issue.code}
        </code>
        <span className="break-words">{issue.message}</span>
      </div>
      {(issue.partIds.length > 0 || issue.jointIds.length > 0) && (
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {issue.partIds.map((id) => (
            <RefChip key={`p-${id}`} kind="part" id={id} />
          ))}
          {issue.jointIds.map((id) => (
            <RefChip key={`j-${id}`} kind="joint" id={id} />
          ))}
        </div>
      )}
      {issue.fix && (
        <div className="mt-1 flex gap-1 text-[10px] font-semibold text-brand-indigo">
          <span>→</span>
          <span>{issue.fix}</span>
        </div>
      )}
    </div>
  );
}

function RefChip({ kind, id }: { kind: 'part' | 'joint'; id: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${kind} id`}
      className={cn(
        'group inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[9px] tracking-wide transition-colors',
        kind === 'part'
          ? 'bg-brand-teal-soft text-brand-indigo [@media(hover:hover)]:hover:bg-brand-teal'
          : 'bg-brand-indigo text-brand-cream [@media(hover:hover)]:hover:bg-brand-indigo-hover',
      )}
    >
      <span className="opacity-60">{kind === 'part' ? 'part' : 'joint'}</span>
      <span className="font-semibold">{id}</span>
      {copied ? (
        <Check className="h-2.5 w-2.5" />
      ) : (
        <Copy className="h-2.5 w-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
      )}
    </button>
  );
}

function severityStyles(severity: ParsedSection['severity']) {
  if (severity === 'error') {
    return { color: 'text-brand-indigo', ring: 'border-brand-indigo', Icon: AlertCircle };
  }
  if (severity === 'warning') {
    return { color: 'text-brand-indigo', ring: 'border-brand-indigo/60', Icon: AlertTriangle };
  }
  return { color: 'text-brand-indigo/70', ring: 'border-brand-indigo/20', Icon: Info };
}
